import React, { ReactElement } from 'react';
import styled from 'styled-components';
import {
  RecordColumn,
  RecordRowPresentation,
} from '../../../common/model/ui/record-list.type';
import Colours from '../../../design-system/colours';
import useRowNavigation from '../../../hooks/use-row-navigation';

interface OvRecordListRowProps<T> {
  record: T;
  columns: RecordColumn<T>[];
  detailsPath?: string;
  getNavigationState?: () => unknown;
  presentation?: RecordRowPresentation;
}

const OvRecordListRow = <T,>({
  record,
  columns,
  detailsPath,
  getNavigationState,
  presentation = {},
}: OvRecordListRowProps<T>): ReactElement => {
  const navigate = useRowNavigation(detailsPath ?? '', getNavigationState);

  return (
    <Row
      $isClickable={!!detailsPath}
      $isFlagged={!!presentation.isFlagged}
      data-muted={presentation.isMuted || undefined}
      onClick={detailsPath ? navigate : undefined}
    >
      {columns.map((column) => (
        <Cell key={column.id} $align={column.align ?? 'left'}>
          {column.renderCell(record, detailsPath)}
        </Cell>
      ))}
    </Row>
  );
};

export default OvRecordListRow;

const Row = styled.tr<{ $isClickable: boolean; $isFlagged: boolean }>`
  ${({ $isClickable }) => $isClickable && 'cursor: pointer;'}

  &:hover {
    background-color: ${Colours.OV_WHITE_HOVER};
  }

  &[data-muted] {
    color: ${Colours.OV_CHARCOAL};
  }

  ${({ $isFlagged }) =>
    $isFlagged &&
    `
    td:first-child {
      box-shadow: inset 3px 0 0 ${Colours.OV_RED};
    }
  `}
`;

const Cell = styled.td<{ $align: 'left' | 'right' }>`
  ${({ $align }) =>
    $align === 'right' &&
    `
    text-align: right;
    font-variant-numeric: tabular-nums;
  `}
`;
